import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addEducation, deleteEducation } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type DropdownOption = { label: string; value: string };

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
  dropdowns?: {
    courseTypes?: DropdownOption[];
  };
};

const MAX_LIMITS: Record<string, number> = {
  degree: 50,
  fieldOfStudy: 50,
  institution: 50,
  university: 50,
  location: 50,
  yearOfPassing: 4,
  grade: 4,
  activities: 200,
  description: 200
};

const numbersOnly = (value: string) => /^[0-9]*$/.test(value);

const EducationForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack,
  dropdowns
}) => {
  const dispatch = useDispatch();

  const educationList = useSelector(
    (state: any) => state.root.resume.education
  );

  const [form, setForm] = useState({
    degree: '',
    fieldOfStudy: '',
    institution: '',
    university: '',
    location: '',
    startDate: '',
    endDate: '',
    yearOfPassing: '',
    grade: '',
    courseType: '',
    activities: '',
    description: ''
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (MAX_LIMITS[name] && value.length > MAX_LIMITS[name]) return;
    if (name === 'yearOfPassing' && !numbersOnly(value)) return;

    const dateFields = ['startDate', 'endDate'];
    const formattedValue =
      dateFields.includes(name) && value
        ? new Date(value).toISOString()
        : value;

    setForm({
      ...form,
      [name]: formattedValue
    });
  };

  const handleBlur = (e: any) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const isAddValid =
    form.degree?.trim() &&
    form.fieldOfStudy?.trim() &&
    form.institution?.trim() &&
    form.courseType?.trim();

  const isContinueValid = educationList.length > 0;

  const getEducationKey = (edu: any) => `${edu.degree}-${edu.institution}`;

  const saveEducation = () => {
    if (!isAddValid) return;

    dispatch(
      addEducation({
        ...form,
        yearOfPassing: Number(form.yearOfPassing)
      })
    );

    setForm({
      degree: '',
      fieldOfStudy: '',
      institution: '',
      university: '',
      location: '',
      startDate: '',
      endDate: '',
      yearOfPassing: '',
      grade: '',
      courseType: '',
      activities: '',
      description: ''
    });
    setTouched({});
  };

  const CharCount = ({
    value,
    max
  }: {
    value: string | undefined;
    max: number;
  }) => {
    const len = value?.length || 0;
    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>
            <h1 className='text-2xl font-semibold'>{stepName}</h1>
            <p className='mt-1 text-sm text-gray-400'>
              Add your educational background
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        {/* FORM */}
        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add Education</h3>

          {/* degree + field of study */}
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Degree <span className='text-red-500'>*</span>
                </label>
                <CharCount value={form.degree} max={50} />
              </div>
              <input
                name='degree'
                value={form.degree}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={50}
                placeholder='Enter degree'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.degree && !form.degree?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.degree && !form.degree?.trim() && (
                <p className='mt-1 text-xs text-red-500'>Degree is required</p>
              )}
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Field of Study <span className='text-red-500'>*</span>
                </label>
                <CharCount value={form.fieldOfStudy} max={50} />
              </div>
              <input
                name='fieldOfStudy'
                value={form.fieldOfStudy}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={50}
                placeholder='Enter specialization'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.fieldOfStudy && !form.fieldOfStudy?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.fieldOfStudy && !form.fieldOfStudy?.trim() && (
                <p className='mt-1 text-xs text-red-500'>
                  Field of study is required
                </p>
              )}
            </div>
          </div>

          {/* institution */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Institution <span className='text-red-500'>*</span>
              </label>
              <CharCount value={form.institution} max={50} />
            </div>
            <input
              name='institution'
              value={form.institution}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={50}
              placeholder='Enter college/university'
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                touched.institution && !form.institution?.trim()
                  ? 'border-red-400 focus:border-red-400'
                  : 'focus:border-gray-300'
              }`}
            />
            {touched.institution && !form.institution?.trim() && (
              <p className='mt-1 text-xs text-red-500'>
                Institution is required
              </p>
            )}
          </div>

          {/* university + location */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>University</label>
                <CharCount value={form.university} max={50} />
              </div>
              <input
                name='university'
                value={form.university}
                onChange={handleChange}
                maxLength={50}
                placeholder='Enter university'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Location</label>
                <CharCount value={form.location} max={50} />
              </div>
              <input
                name='location'
                value={form.location}
                onChange={handleChange}
                maxLength={50}
                placeholder='Enter location'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>

          {/* start date + end date */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>Start Date</label>
              <input
                type='date'
                name='startDate'
                value={form.startDate?.split('T')[0] || ''}
                onChange={handleChange}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <label className='text-sm font-medium'>End Date</label>
              <input
                type='date'
                name='endDate'
                value={form.endDate?.split('T')[0] || ''}
                onChange={handleChange}
                min={form.startDate?.split('T')[0] || ''}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>

          {/* year of passing + grade */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Year of Passing</label>
                <CharCount value={form.yearOfPassing} max={4} />
              </div>
              <input
                name='yearOfPassing'
                value={form.yearOfPassing}
                onChange={handleChange}
                maxLength={4}
                placeholder='YYYY'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Grade / CGPA / Percentage
                </label>
                <CharCount value={form.grade} max={4} />
              </div>
              <input
                name='grade'
                value={form.grade}
                onChange={handleChange}
                maxLength={4}
                placeholder='Enter CGPA/percentage'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>

          {/* course type */}
          <div className='mt-4'>
            <label className='text-sm font-medium'>
              Course Type <span className='text-red-500'>*</span>
            </label>
            <select
              name='courseType'
              value={form.courseType}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                touched.courseType && !form.courseType?.trim()
                  ? 'border-red-400 focus:border-red-400'
                  : 'focus:border-gray-300'
              }`}
            >
              <option value=''>Select course type</option>
              {dropdowns?.courseTypes?.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {touched.courseType && !form.courseType?.trim() && (
              <p className='mt-1 text-xs text-red-500'>
                Course type is required
              </p>
            )}
          </div>

          {/* activities + description */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Activities</label>
                <CharCount value={form.activities} max={200} />
              </div>
              <textarea
                name='activities'
                value={form.activities}
                onChange={handleChange}
                maxLength={200}
                placeholder='Clubs/events'
                rows={3}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Description</label>
                <CharCount value={form.description} max={200} />
              </div>
              <textarea
                name='description'
                value={form.description}
                onChange={handleChange}
                maxLength={200}
                placeholder='Additional details'
                rows={3}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>

          <Button
            label='+ Save Education'
            handleClickBtn={saveEducation}
            variant='contained'
            disabled={!isAddValid}
            classes='mt-5'
          />
        </div>

        {/* ADDED EDUCATION */}
        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Education</h3>

          {educationList.length === 0 ? (
            <p className='text-sm text-gray-400'>
              No education added yet. Add at least one to continue.
            </p>
          ) : (
            <div className='space-y-3'>
              {educationList.map((edu: any) => (
                <div
                  key={getEducationKey(edu)}
                  className='flex items-center justify-between rounded-lg border p-3'
                >
                  <div>
                    <p className='font-medium'>{edu.degree}</p>
                    <p className='text-xs text-gray-500'>
                      {edu.fieldOfStudy} &bull; {edu.institution}
                    </p>
                  </div>

                  <button
                    onClick={() =>
                      dispatch(deleteEducation(getEducationKey(edu)))
                    }
                    className='text-xs text-red-500'
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* footer */}
        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            disabled={!isContinueValid}
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EducationForm;
